import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import KpiCard from "@/components/KpiCard";
import TrustBadge from "@/components/TrustBadge";
import { Users, Stethoscope, Building2, BadgeCheck, Calendar, AlertTriangle, PieChart, MailCheck } from "lucide-react";
import api from "@/lib/api";
import CroReviewQueue from "@/components/admin/CroReviewQueue";
import CareNetHealthCard from "@/components/admin/CareNetHealthCard";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/analytics"),
      ]);
      setStats(s.data);
      setAnalytics(a.data);
    })();
  }, []);

  if (!stats || !analytics) {
    return <AppShell pageTitle="Platform overview"><div className="text-sm text-muted-foreground">Loading…</div></AppShell>;
  }

  const c = stats.counts;

  return (
    <AppShell pageTitle="Platform overview" pageDescription="System-wide health of the Arogya Pramana ecosystem.">
      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total users" value={c.users} icon={Users} tone="sky" testid="admin-kpi-users" />
        <KpiCard label="Doctors" value={c.doctors} unit={`(${c.verified_doctors} verified)`} icon={Stethoscope} tone="mint" testid="admin-kpi-doctors" />
        <KpiCard label="Hospitals" value={c.hospitals} unit={`(${c.verified_hospitals} verified)`} icon={Building2} tone="lavender" testid="admin-kpi-hospitals" />
        <KpiCard label="Appointments" value={c.appointments} icon={Calendar} tone="peach" testid="admin-kpi-appointments" />
      </div>

      {/* Verification funnel */}
      <div className="mt-6 rounded-lg border border-border bg-white p-6" data-testid="verification-funnel-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline inline-flex items-center gap-2"><MailCheck size={12} /> Verification funnel</div>
            <h2 className="mt-1 font-serif text-2xl text-primary">Verified vs pending — by role</h2>
          </div>
          <Link to="/admin/verification" className="text-sm text-primary hover:underline">Open verification queue →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {analytics.role_buckets.map((b) => (
            <RoleBucket key={b.role} bucket={b} />
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          <span><strong className="text-primary">{c.email_verified_users}</strong> email-verified accounts</span>
          <span>·</span>
          <span><strong className="text-primary">{c.email_pending_users}</strong> awaiting email OTP</span>
          <span>·</span>
          <span><strong className="text-accent">{c.pending_professional_verifications}</strong> pending admin credential review</span>
        </div>
      </div>

      {/* Data mining — specialty + organization */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6" data-testid="by-specialty-card">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl text-primary inline-flex items-center gap-2"><PieChart size={16} /> Doctors by specialty</h2>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Trust · verified</span>
          </div>
          <ul className="mt-4 space-y-3" data-testid="by-specialty-list">
            {analytics.by_specialty.slice(0, 8).map((s) => (
              <BarRow
                key={s.specialty}
                label={s.specialty}
                value={s.doctors}
                max={analytics.by_specialty[0]?.doctors || 1}
                right={<>{s.verified}/{s.doctors} · avg {s.avg_trust}</>}
              />
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-border bg-white p-6" data-testid="by-organization-card">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl text-primary">Hospitals by organization</h2>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Trust · incidents</span>
          </div>
          <ul className="mt-4 space-y-3" data-testid="by-organization-list">
            {analytics.by_organization.slice(0, 8).map((o) => (
              <li key={o.organization} className="text-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-primary">{o.organization}</div>
                    <div className="text-xs text-muted-foreground">{o.city} · {o.type} · {o.beds} beds</div>
                  </div>
                  <div className="text-right">
                    <TrustBadge tier={maturity(o.trust_score)} score={o.trust_score} size="sm" />
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {o.incidents_open} open / {o.incidents_total} total
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Appointments by specialty rollup + Category rollup */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6" data-testid="apts-by-specialty-card">
          <h2 className="font-serif text-2xl text-primary">Appointments by specialty</h2>
          <p className="text-xs text-muted-foreground mt-1">Outcomes rollup for care demand mining.</p>
          <ul className="mt-4 space-y-3" data-testid="apts-by-specialty-list">
            {analytics.appointments_by_specialty.length === 0 && (
              <li className="text-xs text-muted-foreground">No appointments recorded yet.</li>
            )}
            {analytics.appointments_by_specialty.slice(0, 8).map((s) => (
              <BarRow
                key={s.specialty}
                label={s.specialty}
                value={s.appointments}
                max={analytics.appointments_by_specialty[0]?.appointments || 1}
              />
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-border bg-white p-6" data-testid="by-category-card">
          <h2 className="font-serif text-2xl text-primary">User categories</h2>
          <p className="text-xs text-muted-foreground mt-1">Bundling for outcome analysis and DPDP-compliant segmentation.</p>
          <ul className="mt-4 divide-y divide-border" data-testid="by-category-list">
            {analytics.by_category.slice(0, 10).map((row) => (
              <li key={row.key} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-primary capitalize">{row.category.replace(/_/g, " ")}</div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{row.role}</div>
                </div>
                <div className="font-mono text-primary">{row.users}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Top providers */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-2xl text-primary">Top doctors by Trust</h2>
            <Link to="/admin/verification" className="text-sm text-primary hover:underline">Verifications →</Link>
          </div>
          <ul className="divide-y divide-border" data-testid="admin-top-doctors">
            {stats.top_doctors.map((d) => (
              <li key={d.id} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-primary">{d.name}</div>
                  <div className="text-xs text-muted-foreground">{d.specialty} · {d.city}</div>
                </div>
                <TrustBadge tier={maturity(d.trust_score)} score={d.trust_score} size="sm" />
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-border bg-white p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-2xl text-primary">Top hospitals by Trust</h2>
          </div>
          <ul className="divide-y divide-border" data-testid="admin-top-hospitals">
            {stats.top_hospitals.map((h) => (
              <li key={h.id} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-primary">{h.name}</div>
                  <div className="text-xs text-muted-foreground">{h.city} · {h.type}</div>
                </div>
                <TrustBadge tier={maturity(h.trust_score)} score={h.trust_score} size="sm" />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Quick links */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/admin/users" className="rounded-lg border border-border bg-white p-5 hover:border-primary transition-colors" data-testid="admin-quick-users">
          <Users size={18} className="text-primary" />
          <div className="mt-3 font-serif text-xl text-primary">Manage users</div>
          <div className="text-xs text-muted-foreground mt-1">Browse all {c.users} accounts</div>
        </Link>
        <Link to="/admin/verification" className="rounded-lg border border-border bg-white p-5 hover:border-primary transition-colors" data-testid="admin-quick-verify">
          <BadgeCheck size={18} className="text-primary" />
          <div className="mt-3 font-serif text-xl text-primary">Verify providers</div>
          <div className="text-xs text-muted-foreground mt-1">Approve doctor &amp; hospital claims</div>
        </Link>
        <Link to="/admin/grievances" className="rounded-lg border border-accent/30 bg-accent/8 p-5 hover:border-accent transition-colors" data-testid="admin-quick-grievances">
          <AlertTriangle size={18} className="text-accent" />
          <div className="mt-3 font-serif text-xl text-primary">DPDP Grievances</div>
          <div className="text-xs text-muted-foreground mt-1">30-day statutory SLA</div>
        </Link>
      </div>

      <div className="mt-8">
        <CareNetHealthCard />
      </div>

      <div className="mt-8">
        <CroReviewQueue />
      </div>

    </AppShell>
  );
}

function RoleBucket({ bucket }) {
  const total = Math.max(bucket.total, 1);
  const emailPct = Math.round((bucket.email_verified / total) * 100);
  const profPct = bucket.prof_verified + bucket.prof_pending > 0
    ? Math.round((bucket.prof_verified / (bucket.prof_verified + bucket.prof_pending)) * 100)
    : null;
  return (
    <div className="rounded-md border border-border bg-background p-4" data-testid={`role-bucket-${bucket.role}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{bucket.role}</div>
      <div className="mt-1 font-serif text-2xl text-primary">{bucket.total}</div>
      <div className="mt-3 text-xs">
        <div className="flex items-center justify-between text-muted-foreground">
          <span>Email OTP</span>
          <span className="text-primary font-medium">{bucket.email_verified}/{bucket.total}</span>
        </div>
        <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary" style={{ width: `${emailPct}%` }} />
        </div>
      </div>
      {profPct !== null && (
        <div className="mt-3 text-xs">
          <div className="flex items-center justify-between text-muted-foreground">
            <span>Credentials</span>
            <span className="text-accent font-medium">{bucket.prof_verified} verified · {bucket.prof_pending} pending</span>
          </div>
          <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-accent" style={{ width: `${profPct}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

function BarRow({ label, value, max, right }) {
  const pct = Math.round((value / max) * 100);
  return (
    <li>
      <div className="flex items-center justify-between text-sm">
        <div className="font-medium text-primary truncate max-w-[60%]">{label}</div>
        <div className="text-xs text-muted-foreground">
          <span className="font-mono text-primary mr-2">{value}</span>{right}
        </div>
      </div>
      <div className="mt-1.5 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </li>
  );
}

function maturity(score) {
  if (score >= 88) return "platinum";
  if (score >= 78) return "gold";
  if (score >= 65) return "silver";
  return "bronze";
}
